<template>
  <section class="idea" ref="root">
    <div class="idea__inner" :class="{ 'idea__inner_visible': inView }">
      <h2 class="idea__headline">В чём идея</h2>
      <p class="idea__lead">
        Это не просто картина.<br />
        Это визуальный фокус на рост, изобилие и результат.
      </p>
      <p class="idea__intro">Мы создаём арт-работы с</p>
      <ul class="idea__list" :class="'idea__list_' + variant">
        <li class="idea__item" v-for="(item, i) in items" :key="item.text" :style="{ transitionDelay: `${0.1 + i * 0.1}s` }">
          <span class="idea__icon" v-html="item.icon" aria-hidden="true" />
          <span class="idea__item-text">{{ item.text }}</span>
        </li>
      </ul>
      <p class="idea__outro">
        Чтобы каждый день напоминать тебе<br />
        <strong>Твой масштаб — больше</strong>
      </p>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'

export default {
  name: 'HomeIdea',
  props: {
    // 'cards' — карточки в ряд, 'strips' — полоски с акцентом слева
    variant: { type: String, default: 'cards', validator: v => ['cards', 'strips'].includes(v) }
  },
  setup() {
    const root = ref(null)
    const inView = ref(false)
    let observer = null

    const items = [
      {
        text: 'образами денег',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>`
      },
      {
        text: 'сильными фразами',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`
      },
      {
        text: 'символами успеха',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15 9 22 9 17 14 19 21 12 17 5 21 7 14 2 9 9 9"/></svg>`
      }
    ]

    onMounted(() => {
      observer = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) inView.value = true },
        { threshold: 0.2, rootMargin: '0px 0px -60px 0px' }
      )
      if (root.value) observer.observe(root.value)
    })
    onUnmounted(() => observer?.disconnect())

    return { root, inView, items }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/_variables.scss";

$idea-duration: 0.7s;
$idea-ease: cubic-bezier(0.22, 1, 0.36, 1);

.idea {
  position: relative;
  padding: 100px $gap 100px;
  background: $color-bg;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-image:
      radial-gradient(ellipse 80% 50% at 50% 0%, rgba(0, 0, 0, 0.03) 0%, transparent 50%),
      url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='0.08'%3E%3Ccircle cx='4' cy='4' r='1.5'/%3E%3Ccircle cx='20' cy='4' r='1.5'/%3E%3Ccircle cx='4' cy='20' r='1.5'/%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/svg%3E");
  }

  @media (max-width: $breakpoint-mobile) {
    padding: 64px $gap 72px;
  }

  &__inner {
    position: relative;
    z-index: 1;
    max-width: $container-max;
    margin: 0 auto;

    &_visible .idea__headline { opacity: 1; transform: translateY(0); }
    &_visible .idea__lead { opacity: 1; transform: translateY(0); }
    &_visible .idea__intro { opacity: 1; transform: translateY(0); }
    &_visible .idea__item { opacity: 1; transform: translateY(0) translateX(0); }
    &_visible .idea__outro { opacity: 1; transform: translateY(0); }
  }

  &__headline {
    font-size: clamp(14px, 1.5vw, 16px);
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: $color-text-muted;
    margin: 0 0 20px;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity $idea-duration $idea-ease, transform $idea-duration $idea-ease;
  }

  &__lead {
    font-size: clamp(24px, 3.5vw, 36px);
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.02em;
    color: $color-text;
    margin: 0 0 48px;
    max-width: 640px;
    opacity: 0;
    transform: translateY(24px);
    transition: opacity $idea-duration $idea-ease 0.05s, transform $idea-duration $idea-ease 0.05s;

    @media (max-width: $breakpoint-mobile) {
      margin-bottom: 36px;
    }
  }

  &__intro {
    font-size: clamp(18px, 2vw, 22px);
    font-weight: 600;
    color: $color-text;
    margin: 0 0 28px;
    text-align: center;
    letter-spacing: 0.01em;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity $idea-duration $idea-ease 0.1s, transform $idea-duration $idea-ease 0.1s;
  }

  &__list {
    list-style: none;
    margin: 0 0 56px;
    padding: 0;
    display: grid;
    gap: 0;

    @media (min-width: 769px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 24px;
    }

    @media (max-width: $breakpoint-mobile) {
      margin-bottom: 40px;
      gap: 16px;
    }

    &_strips {
      grid-template-columns: 1fr;
      gap: 0;
      max-width: 640px;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 56px;
    }
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    padding: 28px 24px;
    background: #fafafa;
    border: 1px solid $color-border;
    border-radius: 12px;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity $idea-duration $idea-ease, transform $idea-duration $idea-ease,
      border-color 0.2s, box-shadow 0.2s;

    &:hover {
      border-color: rgba(0, 0, 0, 0.15);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
    }

    @media (max-width: $breakpoint-mobile) {
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: 24px 20px;
      gap: 14px;
    }

    .idea__list_strips & {
      background: transparent;
      border: none;
      border-radius: 0;
      padding: 20px 0;
      border-left: 4px solid $color-primary;
      padding-left: 24px;
      margin-left: 0;
      transform: translateX(-16px);
      box-shadow: none;

      &:hover {
        border-left-color: $color-text-muted;
      }

      @media (max-width: $breakpoint-mobile) {
        padding-left: 20px;
        align-items: flex-start;
        text-align: left;
      }
    }
  }

  &__icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: $color-primary;
    color: $color-primary-inverse;
    border-radius: 10px;

    :deep(svg) {
      width: 26px;
      height: 26px;
    }

    @media (max-width: $breakpoint-mobile) {
      width: 44px;
      height: 44px;
      :deep(svg) { width: 22px; height: 22px; }
    }

    .idea__list_strips & {
      width: 40px;
      height: 40px;
      background: transparent;
      color: $color-primary;
      border: 2px solid $color-primary;
      border-radius: 50%;

      :deep(svg) {
        width: 20px;
        height: 20px;
      }

      @media (max-width: $breakpoint-mobile) {
        width: 36px;
        height: 36px;
        :deep(svg) { width: 18px; height: 18px; }
      }
    }
  }

  &__item-text {
    font-size: clamp(17px, 1.8vw, 20px);
    font-weight: 600;
    line-height: 1.35;
    color: $color-text;
    padding-top: 4px;

    .idea__list_strips & {
      padding-top: 6px;
    }
  }

  &__outro {
    font-size: clamp(17px, 1.8vw, 20px);
    line-height: 1.6;
    color: $color-text-muted;
    margin: 0;
    text-align: center;
    max-width: 520px;
    margin-left: auto;
    margin-right: auto;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity $idea-duration $idea-ease 0.2s, transform $idea-duration $idea-ease 0.2s;

    strong {
      display: block;
      margin-top: 12px;
      font-size: clamp(22px, 2.8vw, 28px);
      font-weight: 800;
      color: $color-accent-teal; // accent teal
      letter-spacing: -0.02em;
    }
  }
}
</style>
